import { useMemo, useState } from 'react';
import type { DataISO, Dipendente, Evento, ProgrammaSettimanale } from '../api/tipi.ts';
import { indicizzaEventi, indicizzaProgrammi } from '../calendario/griglia.ts';
import { lunediDi } from '../calendario/controlli.ts';
import { useSchermoLargo } from '../app/useSchermoLargo.ts';
import stili from './Funzionari.module.css';

/**
 * Riepilogo settimanale dei funzionari.
 *
 * E' l'anteprima della tabella che parte via email il venerdi' alle 17:00:
 * righe = funzionari, colonne = giorni da lunedi' a venerdi'.
 *
 * La regola di composizione della cella e' quella ricavata dal foglio
 * `Settimana funzionari` e verificata sui 12 funzionari di una settimana
 * campione:
 *
 *     cella = MAIUSCOLO( unione con " / " di [evento, programma] non vuoti )
 *
 * Averla anche a schermo serve a due cose: i funzionari vedono cosa uscira'
 * prima delle 16:30, e chi la riceve puo' controllarla senza aspettare il
 * venerdi'.
 */

export interface ProprietaFunzionari {
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
  programmi: readonly ProgrammaSettimanale[];
}

const NOMI_GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];
const NOMI_GIORNI_BREVI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven'];

export function Funzionari({ dipendenti, eventi, programmi }: ProprietaFunzionari) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const schermoLargo = useSchermoLargo();

  // Solo per il telefono: quale giorno della settimana si sta guardando. Resta
  // lo stesso cambiando settimana - chi guarda il martedi' e va avanti vuole
  // il martedi' dopo, non tornare al lunedi'.
  const [giornoScelto, setGiornoScelto] = useState(0);

  // Si parte dalla settimana SUCCESSIVA: e' quella che i funzionari devono
  // compilare e quella che verra' spedita.
  const [lunedi, setLunedi] = useState<DataISO>(() => sommaGiorni(lunediDi(oggi), 7));

  /*
   * L'ordine arriva dal server: sede, reparto, nome.
   *
   * Riordinare qui per nome della sede metteva Cambiano prima di Verona,
   * mentre la mail del venerdi' segue l'ordine delle sedi deciso in azienda.
   * Da quando le righe sono i funzionari, l'ordine e' la prima cosa che si
   * usa per ritrovarsi: le due tabelle devono elencarli allo stesso modo.
   */
  const funzionari = useMemo(
    () => dipendenti.filter((d) => d.ruolo === 'FUNZIONARIO' && d.stato === 'ATTIVO'),
    [dipendenti],
  );

  const indiceEventi = useMemo(() => indicizzaEventi(eventi), [eventi]);
  const indiceProgrammi = useMemo(() => indicizzaProgrammi(programmi), [programmi]);

  const giorni = useMemo(
    () => [0, 1, 2, 3, 4].map((i) => sommaGiorni(lunedi, i)),
    [lunedi],
  );

  const venerdi = giorni[4]!;

  /*
   * Il testo di una cella, per la tabella e per l'elenco del telefono.
   *
   * Scritto una volta sola: le due viste sono due modi di mostrare la stessa
   * tabella, e se ognuna avesse la sua copia della regola prima o poi il
   * telefono direbbe una cosa e lo schermo grande un'altra - lo stesso
   * difetto che per mesi ha separato questa pagina dalla mail del venerdi'.
   */
  const testoCella = (idFunzionario: string, giorno: DataISO): string =>
    contenutoCella(
      // Un giorno puo' avere assenza e trasferta insieme: si mostrano
      // entrambe invece di perderne una.
      (indiceEventi.get(`${idFunzionario}|${giorno}`) ?? [])
        .map(descrizioneEvento)
        .join(' + ') || null,
      indiceProgrammi.get(`${idFunzionario}|${giorno}`) ?? null,
    );
  const daCompilare = funzionari.filter(
    (f) => !giorni.some((g) => indiceProgrammi.get(`${f.id}|${g}`)),
  );
  /**
   * Prima riga di ogni sede: prende un filo piu' marcato sopra.
   *
   * L'elenco e' ordinato per sede, e il filo segna dove finisce un gruppo e
   * comincia il successivo. Non colora nulla, quindi non interferisce con
   * l'alternanza delle righe.
   */
  const primoDiSede = useMemo(() => {
    const primi = new Set<string>();
    let sedeCorrente: string | null = null;
    for (const f of funzionari) {
      if (f.sedeNome !== sedeCorrente) {
        primi.add(f.id);
        sedeCorrente = f.sedeNome;
      }
    }
    return primi;
  }, [funzionari]);

  return (
    <div className={stili.pagina}>
      <header className={stili.testata}>
        <div>
          <h1 className={stili.titolo}>Riepilogo settimanale funzionari</h1>
          <p className={stili.sottotitolo}>
            Settimana dal {italiana(lunedi)} al {italiana(venerdi)} · anteprima della
            tabella inviata il venerdì alle 17:00
          </p>
        </div>
        <div className={stili.frecce}>
          <button
            type="button"
            className={stili.freccia}
            onClick={() => setLunedi((l) => sommaGiorni(l, -7))}
            aria-label="Settimana precedente"
          >
            ‹
          </button>
          <button
            type="button"
            className={stili.pulsante}
            onClick={() => setLunedi(sommaGiorni(lunediDi(oggi), 7))}
          >
            Prossima settimana
          </button>
          <button
            type="button"
            className={stili.freccia}
            onClick={() => setLunedi((l) => sommaGiorni(l, 7))}
            aria-label="Settimana successiva"
          >
            ›
          </button>
        </div>
      </header>

      {daCompilare.length > 0 && (
        <p className={stili.avviso}>
          {daCompilare.length === 1
            ? 'Non ha ancora compilato: '
            : `Non hanno ancora compilato (${daCompilare.length}): `}
          {daCompilare.map((f) => f.nomeCompleto).join(', ')}
        </p>
      )}

      {/*
        Una persona per riga, un giorno per colonna.
        La tabella nel foglio Excel era girata al contrario, ma con dodici o
        piu' funzionari e cinque giorni quella forma produceva cinque righe
        lunghissime da scorrere in orizzontale. Cosi' invece l'elenco dei nomi
        si legge in verticale, come ogni altro elenco di persone
        nell'applicazione, e la settimana sta tutta nello schermo.
      */}
      {!schermoLargo && (
        /*
          Sul telefono: un giorno per volta.

          La tabella ha sei colonne e tredici righe, e su uno schermo stretto
          obbliga a scorrere in due direzioni per leggere una cella sola. Da
          fuori ufficio la domanda e' quasi sempre una - dove sara' questa
          persona quel giorno - e un elenco di nomi con accanto il posto ci
          risponde senza scorrere di lato.
        */
        <>
          <div className={stili.giorni} role="tablist" aria-label="Giorno della settimana">
            {giorni.map((giorno, i) => (
              <button
                key={giorno}
                type="button"
                role="tab"
                aria-selected={i === giornoScelto}
                className={`${stili.giorno} ${i === giornoScelto ? stili.giornoAttivo : ''}`}
                onClick={() => setGiornoScelto(i)}
              >
                <span className={stili.giornoNome}>{NOMI_GIORNI_BREVI[i]}</span>
                <span className={stili.giornoData}>{italiana(giorno).slice(0, 5)}</span>
              </button>
            ))}
          </div>

          <p className={stili.giornoTitolo}>
            {NOMI_GIORNI[giornoScelto]} {italiana(giorni[giornoScelto]!)}
          </p>

          <ul className={stili.elenco}>
            {funzionari.map((f) => {
              const testo = testoCella(f.id, giorni[giornoScelto]!);
              return (
                <li
                  key={f.id}
                  className={`${stili.voce} ${primoDiSede.has(f.id) ? stili.voceInizioSede : ''}`}
                >
                  <span className={stili.voceChi}>
                    <span className={stili.nome}>{f.nomeCompleto}</span>
                    <span className={stili.sede}>{f.sedeNome}</span>
                  </span>
                  <span className={testo === '' ? stili.voceVuota : stili.vocePiena}>
                    {testo === '' ? '—' : testo}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {schermoLargo && (
      <div className={stili.scorrevole}>
        <table className={stili.tabella}>
          <thead>
            <tr>
              <th className={stili.colonnaFunzionario}>Funzionario</th>
              {giorni.map((giorno, i) => (
                <th key={giorno}>
                  <span className={stili.nomeGiorno}>{NOMI_GIORNI[i]}</span>
                  <span className={stili.dataGiorno}>{italiana(giorno)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {funzionari.map((f) => (
              <tr
                key={f.id}
                className={primoDiSede.has(f.id) ? stili.inizioSede : undefined}
              >
                <th scope="row" className={stili.colonnaFunzionario}>
                  <span className={stili.nome}>{f.nomeCompleto}</span>
                  <span className={stili.sede}>{f.sedeNome}</span>
                </th>
                {giorni.map((giorno) => {
                  const testo = testoCella(f.id, giorno);
                  return (
                    <td key={giorno} className={testo === '' ? stili.vuota : stili.piena}>
                      {testo}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {funzionari.length === 0 && (
        <p className={stili.vuoto}>Nessun funzionario in anagrafica.</p>
      )}
    </div>
  );
}

/**
 * Di uno spostamento interessa DOVE, non che sia uno spostamento.
 *
 * Scrivere "SPOSTAMENTO" in una tabella che serve a sapere dove sara'
 * ciascun funzionario la settimana prossima non risponde alla domanda per cui
 * la tabella esiste. La destinazione sta nella nota, ed e' quella che va in
 * cella. Il tipo si scrive solo se la nota manca: uno spostamento senza
 * destinazione e' un dato incompleto, ma nasconderlo sarebbe peggio che
 * mostrarlo generico.
 *
 * Deve restare identica a quella del job che manda la mail del venerdi'
 * (`backend/src/jobs/matrice-settimanale.ts`): sono la stessa tabella, e se
 * le due divergono chi la riceve legge una cosa diversa da chi la compila.
 */
export function descrizioneEvento(evento: Evento): string {
  if (evento.tipo === 'SPOSTAMENTO') {
    const dove = evento.nota?.trim();
    if (dove !== undefined && dove !== '') return dove;
  }
  return evento.etichetta;
}

/** La regola ricavata dal foglio `Settimana funzionari`. */
export function contenutoCella(evento: string | null, programma: string | null): string {
  return [evento, programma]
    .filter((p): p is string => p !== null && p.trim() !== '')
    .join(' / ')
    .toUpperCase();
}

function sommaGiorni(data: DataISO, giorni: number): DataISO {
  return new Date(Date.parse(`${data}T00:00:00Z`) + giorni * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

function italiana(data: DataISO): string {
  return data.split('-').reverse().join('/');
}
